import { registerPlugin } from '@capacitor/core';

/**
 * Puente TypeScript del plugin nativo `RoisBackgroundLocation`
 * (`android/app/src/main/java/com/insolite/rois/geo/`).
 *
 * Existe porque ningún plugin gratuito de background-geolocation para
 * Capacitor persiste las posiciones en nativo: todos las emiten al callback
 * JS, que es exactamente lo que Android congela en segundo plano. Nuestro
 * servicio graba cada fix en SQLite ANTES de tocar el bridge, así que el punto
 * sobrevive aunque el WebView esté congelado o el proceso muerto.
 *
 * Contrato de drenaje (ver `positionSource.ts`): `drain` NO borra; borra `ack`,
 * y solo después de que el punto esté en IndexedDB. Si la app muere entre
 * ambos, se relee y la deduplicación por timestamp de `appendGpsPoints` lo
 * absorbe.
 */

/** Punto tal cual sale del nativo. `timestamp` en epoch ms (no ISO). */
export interface NativeGpsPoint {
  latitude: number;
  longitude: number;
  /** Epoch en milisegundos — la conversión a ISO-8601 la hace `positionSource`. */
  timestamp: number;
  accuracy?: number;
  speed?: number;
  bearing?: number;
}

export interface BackgroundPermissionStatus {
  granted: boolean;
  /**
   * Etiqueta REAL del ajuste en este dispositivo, vía
   * `getBackgroundPermissionOptionLabel()`. Cambia entre versiones de Android
   * y entre fabricantes ("Permitir siempre" en la Tab Active5, "Permitir todo
   * el tiempo" en otras), así que nunca se escribe a mano.
   */
  optionLabel: string;
}

/**
 * Estado de los permisos declarados por el plugin. Lo genera Capacitor solo, a
 * partir de las anotaciones `@Permission` del plugin nativo — no hay que
 * implementar nada en Java.
 *
 * La distinción que importa aquí es `'prompt'` frente a `'denied'`: Android
 * NO las diferencia en la pantalla de Ajustes, donde un permiso que jamás se
 * ha pedido aparece igual de «No permitido» que uno rechazado a conciencia.
 */
export interface RoisPermissionStatus {
  /** `'prompt'` = todavía no se ha preguntado nunca. */
  location: 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale';
  notifications: 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale';
}

export interface RoisBackgroundLocationPlugin {
  /**
   * ¿Está concedido el permiso de ubicación en SEGUNDO PLANO?
   *
   * No condiciona la captura —mientras el servicio corre, Android no considera
   * que la app esté en background— sino la RECUPERACIÓN: recrear el servicio
   * si el sistema lo mata, y reanudar tras reiniciar la tablet.
   */
  checkBackgroundPermission(): Promise<BackgroundPermissionStatus>;
  /**
   * Estado de los permisos de primer plano. Lo implementa Capacitor por su
   * cuenta; se declara aquí solo para poder llamarlo con tipos.
   *
   * Sirve para saber si al operario ya se le ha PREGUNTADO. Los permisos se
   * piden dentro de `start()`, o sea al iniciar un tramo, así que en una
   * instalación recién hecha no se ha pedido nada todavía.
   */
  checkPermissions(): Promise<RoisPermissionStatus>;
  /**
   * ¿Está encendida la ubicación DEL SISTEMA? No es lo mismo que el permiso.
   *
   * Con el permiso concedido y la ubicación del aparato apagada, el servicio
   * sigue «corriendo» y deja de emitir posiciones sin error ninguno. Y no vale
   * mirar si llegan puntos: el servicio solo emite al moverse 5 m, así que el
   * silencio es también lo normal en una actuación de veinte minutos parado.
   *
   * Esta es la única señal que distingue las dos cosas.
   */
  isLocationEnabled(): Promise<{ enabled: boolean }>;
  /**
   * Pide permisos al sistema. Lo implementa Capacitor por su cuenta a partir de
   * las anotaciones `@Permission` del plugin nativo; se declara aquí solo para
   * poder llamarlo con tipos — **no hay una línea de Java detrás de esto**, y
   * por eso el cambio que lo usa viaja por OTA sin exigir una APK nueva.
   *
   * `permissions` acota qué alias se piden. Sin él se piden TODOS, que aquí es
   * justo lo que no se quiere: la ubicación tiene su momento (`start()`, al
   * iniciar un tramo) y adelantarla sacaría un diálogo de GPS a alguien que
   * todavía no va a capturar nada.
   */
  requestPermissions(options?: {
    permissions?: Array<'location' | 'notifications'>;
  }): Promise<RoisPermissionStatus>;
  /**
   * Abre la ficha de ajustes de la app. Desde Android 11 es la ÚNICA vía: el
   * diálogo del sistema ya no ofrece la opción de permitir siempre.
   */
  openAppSettings(): Promise<void>;
  /**
   * Arranca el foreground service. Debe llamarse con la app en primer plano
   * (el operario pulsa "Iniciar tramo"): Android 14+ prohíbe crear un servicio
   * de tipo `location` desde background salvo con `ACCESS_BACKGROUND_LOCATION`.
   */
  start(options: {
    routeId: string;
    notificationTitle?: string;
    notificationText?: string;
    intervalMs?: number;
    /**
     * Filtro de distancia NATIVO, deliberadamente grueso. El filtro bueno
     * (adaptativo a la precisión + keepalive) vive en
     * `useSurveillanceRouteGpsTracking` y debe seguir viviendo en un solo
     * sitio.
     */
    distanceFilter?: number;
  }): Promise<void>;

  stop(): Promise<void>;

  isRunning(): Promise<{ running: boolean }>;

  /** Lee los puntos pendientes SIN borrarlos. */
  drain(options: { routeId: string }): Promise<{ points: NativeGpsPoint[] }>;

  /** Borra los puntos ya persistidos en IndexedDB (epoch ms, inclusive). */
  ack(options: { routeId: string; upToTs: number }): Promise<{ deleted: number }>;

  getPendingCount(options: { routeId: string }): Promise<{ count: number }>;

  addListener(
    eventName: 'location',
    listenerFunc: (point: NativeGpsPoint) => void
  ): Promise<{ remove: () => Promise<void> }>;
}

export const RoisBackgroundLocation = registerPlugin<RoisBackgroundLocationPlugin>(
  'RoisBackgroundLocation'
);

export default RoisBackgroundLocation;
